<template>
  <div class="kanban-board">
    <div class="kanban-columns">
      <div v-for="status in statuses" :key="status.value" class="kanban-column">
        <div class="kanban-column-header" :style="{backgroundColor: status.color}">
          <h6 class="mb-0 text-white">{{ status.label }}</h6>
          <span class="badge bg-white text-dark">{{ getTasksByStatus(status.value).length }}</span>
        </div>
        <div 
          class="kanban-column-body"
          @drop="onDrop($event, status.value)"
          @dragover="onDragOver($event)"
          @dragenter="onDragEnter($event)"
          @dragleave="onDragLeave($event)"
        >
          <div 
            v-for="task in getTasksByStatus(status.value)" 
            :key="task.id" 
            class="kanban-card"
            :draggable="helper.checkAccess('edit_tasks') > 0"
            @dragstart="onDragStart($event, task)"
            @dragend="onDragEnd($event)"
            @click="viewTask(task)"
          >
            <div class="task-card-heading">
              <span class="task-state-icon" :style="getStatusIconStyle(task.status)">
                <AppIcon :name="getStatusIcon(task.status)" class="bi" :class="getStatusIcon(task.status)" />
              </span>
              <strong class="task-title">{{ task.title }}</strong>
            </div>
            <p class="task-description">{{ task.description || $t('no_description') }}</p>

            <div class="task-card-footer">
              <div class="task-assignee">
                <span class="assignee-avatar">{{ assigneeInitial(task) }}</span>
                <span class="assignee-name">{{ assigneeName(task) }}</span>
                <span v-if="task.assignments && task.assignments.length > 1" class="assignee-count">+{{ task.assignments.length - 1 }}</span>
              </div>
              <span class="task-due-date" :class="{ overdue: isOverdue(task.due_date) }">
                <AppIcon name="bi-calendar3" class="bi bi-calendar3" />
                {{ formatDate(task.due_date) }}
              </span>
            </div>
          </div>
          <div v-if="!getTasksByStatus(status.value).length" class="text-center text-muted py-3">
            <small>{{$t('no_tasks')}}</small>
          </div>
        </div>
      </div>
    </div>

    <!-- Task Detail Modal -->
    <task-detail-modal v-if="selectedTask" :task="selectedTask" @close="selectedTask = null" @refresh="loadTasks"></task-detail-modal>

  </div>
</template>

<script>
import helper from '../../helper'
import TaskDetailModal from './TaskDetailModal.vue'
import { getKanbanStatuses } from '../../helpers/taskStatusHelper'

export default {
  name: 'TaskKanban',
  components: {
    TaskDetailModal
  },
  props: {
    projectId: {
      type: String,
      required: true
    },
    tasks: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      helper,
      localTasks: [],
      loading: false,
      selectedTask: null,
      draggedItem: null
    }
  },
  computed: {
    statuses() {
      return getKanbanStatuses(this.$store);
    }
  },
  watch: {
    tasks: {
      handler(val) {
        this.localTasks = val
      },
      immediate: true,
      deep: true
    }
  },
  mounted() {
    // Tasks are loaded via prop
  },
  methods: {
    async loadTasks() {
      this.$emit('refresh')
    },
    // Drag and Drop Methods
    onDragStart(event, task) {
      if (helper.checkAccess('edit_tasks') <= 0) return
      this.draggedItem = task
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/html', event.target.innerHTML)
      event.target.classList.add('dragging')
    },
    onDragEnd(event) {
      event.target.classList.remove('dragging')
    },
    onDragOver(event) {
      event.preventDefault()
      event.dataTransfer.dropEffect = 'move'
    },
    onDragEnter(event) {
      if (event.target.classList.contains('kanban-column-body')) {
        event.target.classList.add('drag-over')
      }
    },
    onDragLeave(event) {
      if (event.target.classList.contains('kanban-column-body')) {
        event.target.classList.remove('drag-over')
      }
    },
    async onDrop(event, newStatus) {
      event.preventDefault()
      event.target.classList.remove('drag-over')
      
      if (helper.checkAccess('edit_tasks') <= 0 || !this.draggedItem) return
      
      const task = this.draggedItem
      const oldStatus = task.status
      const oldProgress = task.progress
      
      // Don't do anything if dropped in the same column
      if (oldStatus === newStatus) {
        this.draggedItem = null
        return
      }
      
      // Optimistic update
      task.status = newStatus
      if (newStatus === 'completed' && task.progress !== 100) {
        task.progress = 100
      }

      try {
        // Update task status via API without blocking UI
        helper.customPatch(`${process.env.VUE_APP_API}/tasks/${task.id}`, {
          status: newStatus
        }, { showToast: true }).then(() => {
          // If moving to completed, set progress to 100%
          if (newStatus === 'completed' && oldProgress !== 100) {
            helper.customPatch(`${process.env.VUE_APP_API}/tasks/${task.id}`, {
              progress: 100
            }).then(() => this.loadTasks())
          } else {
            this.loadTasks()
          }
        }).catch(error => {
          // Revert on error
          task.status = oldStatus
          task.progress = oldProgress
          alert('Error updating task status: ' + error)
          this.loadTasks()
        })
        
        // Reset dragged item
        this.draggedItem = null
      } catch (error) {
        // Revert on error
        task.status = oldStatus
        task.progress = oldProgress
        alert('Error updating task status: ' + error)
        this.draggedItem = null
      }
    },
    getTasksByStatus(status) {
      return this.localTasks.filter(task => task.status === status)
    },
    async viewTask(task) {
      if (helper.checkAccess('view_tasks') <= 0) return
      try {
        const response = await helper.customGet(`${process.env.VUE_APP_API}/tasks/${task.id}`)
        this.selectedTask = response.data
      } catch (error) {
        alert('Error loading task details:', error)
      }
    },
    getStatusIcon(status) {
      if (status === 'completed') return 'bi-check-lg'
      if (status === 'in_progress' || status === 'review') return 'bi-clock'
      if (status === 'cancelled') return 'bi-x-lg'
      return 'bi-circle-fill'
    },
    getStatusIconStyle(statusValue) {
      const status = this.statuses.find(item => item.value === statusValue)
      const color = status ? status.color : '#64748b'
      return { backgroundColor: color, boxShadow: `0 5px 12px ${color}33` }
    },
    assigneeName(task) {
      const assignment = task.assignments && task.assignments.find(item => item.user)
      return assignment && assignment.user && assignment.user.name ? assignment.user.name : this.$t('unassigned')
    },
    assigneeInitial(task) {
      const name = this.assigneeName(task)
      return name ? name.split(' ').map(part => part.charAt(0)).join('').slice(0, 2).toUpperCase() : '?'
    },
    formatDate(date) {
      if (!date) return '-'
      return new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' }).format(new Date(date))
    },
    isOverdue(dueDate) {
      if (!dueDate) return false
      return new Date(dueDate) < new Date()
    }
  }
}
</script>

<style scoped>
.kanban-board {
  overflow-x: auto;
  padding-bottom: 20px;
}

.kanban-columns {
  display: flex;
  gap: 12px;
  min-width: 100%;
}

.kanban-column {
  flex: 1;
  min-width: 250px;
  background: #f4f7fb;
  border-radius: 10px;
  overflow: hidden;
}

.kanban-column-header {
  padding: 12px 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.kanban-column-body {
  padding: 10px;
  min-height: 400px;
  max-height: 600px;
  overflow-y: auto;
}

.kanban-card {
  background: white;
  border: 1px solid #e8edf4;
  border-radius: 11px;
  padding: 12px;
  margin-bottom: 10px;
  box-shadow: 0 2px 8px rgba(31, 45, 61, 0.06);
  cursor: move;
  transition: all 0.2s;
}

.kanban-card:hover {
  border-color: #d7e2f0;
  box-shadow: 0 5px 14px rgba(31, 45, 61, 0.1);
  transform: translateY(-1px);
}

.kanban-card.dragging {
  opacity: 0.5;
  cursor: grabbing;
}

.kanban-column-body.drag-over {
  background-color: #e3f2fd;
  border: 2px dashed #2196F3;
}

.task-card-heading {
  display: flex;
  align-items: center;
  gap: 9px;
}

.task-state-icon {
  display: inline-grid;
  width: 29px;
  height: 29px;
  flex: 0 0 29px;
  place-items: center;
  border-radius: 50%;
  color: #fff;
  font-size: 13px;
}

.task-state-icon .bi-circle-fill {
  font-size: 9px;
}

.task-title {
  color: #17233a;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.task-description {
  min-height: 18px;
  margin: 6px 0 10px 38px;
  color: #6b7c93;
  font-size: 11px;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.task-card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-left: 38px;
}

.task-assignee {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 6px;
}

.assignee-avatar {
  display: grid;
  width: 27px;
  height: 27px;
  flex: 0 0 27px;
  place-items: center;
  border-radius: 50%;
  background: #e4ddff;
  color: #6750a4;
  font-size: 9px;
  font-weight: 750;
}

.assignee-name {
  overflow: hidden;
  color: #4e5f75;
  font-size: 11px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.assignee-count {
  color: #8795a8;
  font-size: 9px;
  font-weight: 650;
}

.task-due-date {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 5px;
  color: #526781;
  font-size: 11px;
  font-weight: 600;
}

.task-due-date i {
  font-size: 14px;
}

.task-due-date.overdue {
  color: #dc3545;
}
</style>
